import React, { useCallback } from 'react';
import { FieldLabel } from '@puckeditor/core';

import { isTextColorRole } from '../tokens';
import { hexColorSwatchValue } from '../hex-color';
import { HexColorError, useHexColorInput } from './use-hex-color-input';
import type { TextColorRole } from '../tokens';
import {
  FIELD_CONTROL_FONT_SIZE,
  FIELD_CONTROL_PADDING,
  FIELD_CONTROL_RADIUS,
  fieldControlStyle,
  selectControlStyle,
  disabledSelectControlStyle,
} from './field-control-style';

type ColorRoleFieldProps = {
  field: { label?: string };
  name: string;
  /**
   * Undefined when the prop is absent from saved page data — a component may make its color
   * optional so it can fall back to another color for pages authored before the prop existed
   * (see faq.tsx's headingColor). Treated as "no role selected", i.e. custom.
   */
  value: string | undefined;
  onChange: (value: string) => void;
  readOnly?: boolean;
};

const ROLE_OPTIONS: readonly { label: string; value: TextColorRole }[] = [
  { label: 'Text', value: 'text' },
  { label: 'Secondary', value: 'secondary' },
  { label: 'Muted', value: 'muted' },
  { label: 'Inverted', value: 'inverted' },
  { label: 'Link', value: 'link' },
];

const CUSTOM_OPTION_VALUE = 'custom';

/** Starting color when switching from a theme role to a custom value. */
const CUSTOM_DEFAULT = '#121516';

const dropdownStyle: React.CSSProperties = {
  ...selectControlStyle,
  padding: `var(--dcm-cms-color-role-select-padding, ${FIELD_CONTROL_PADDING})`,
  paddingRight: selectControlStyle.paddingRight,
  borderColor:
    'var(--dcm-cms-color-role-border-color, var(--puck-field-color-border, var(--puck-color-border, #d0d4d8)))',
  borderRadius: `var(--dcm-cms-color-role-border-radius, ${FIELD_CONTROL_RADIUS})`,
  fontSize: `var(--dcm-cms-color-role-font-size, ${FIELD_CONTROL_FONT_SIZE})`,
  color: 'var(--dcm-cms-color-role-text-color, var(--puck-field-color-text, var(--puck-color-text, #121516)))',
  backgroundColor: 'var(--dcm-cms-color-role-bg, var(--puck-field-color-bg, #fff))',
};

/**
 * Puck custom field for text color, offering the theme's semantic color roles plus
 * a custom hex escape hatch via a dropdown. Roles are stored by key so the rendered
 * color keeps tracking merchant branding; a custom value is stored as the literal hex.
 */
export function ColorRoleField({ field, name, value = '', onChange, readOnly }: ColorRoleFieldProps): JSX.Element {
  const isCustom = !isTextColorRole(value);
  const hexInput = useHexColorInput(value, onChange);

  const selectValue = isCustom ? CUSTOM_OPTION_VALUE : value;

  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const chosen = e.currentTarget.value;
      if (chosen === CUSTOM_OPTION_VALUE) {
        // Only seed the starting hex when not already in custom mode.
        if (!isCustom) onChange(CUSTOM_DEFAULT);
      } else {
        onChange(chosen);
      }
    },
    [isCustom, onChange]
  );

  const labelText = field.label ?? name;

  return (
    <FieldLabel label={labelText} readOnly={readOnly}>
      <select
        value={selectValue}
        onChange={handleSelectChange}
        disabled={readOnly}
        aria-label={labelText}
        data-testid={`color-role-select-${name}`}
        style={{ ...dropdownStyle, ...(readOnly && disabledSelectControlStyle) }}
      >
        {ROLE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
        <option value={CUSTOM_OPTION_VALUE}>Custom</option>
      </select>

      {isCustom && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
          <input
            type='color'
            value={hexColorSwatchValue(value, CUSTOM_DEFAULT)}
            onChange={hexInput.handleSwatchChange}
            disabled={readOnly}
            aria-label={`${labelText} custom color swatch`}
            data-testid={`color-role-swatch-${name}`}
            style={{
              width: 32,
              height: 32,
              padding: 0,
              border: `1px solid var(--dcm-cms-color-role-border-color, #d0d4d8)`,
              borderRadius: 'var(--dcm-cms-color-role-border-radius, 4px)',
              cursor: 'pointer',
            }}
          />
          <input
            type='text'
            value={hexInput.text}
            onChange={hexInput.handleChange}
            disabled={readOnly}
            aria-label={`${labelText} custom color value`}
            aria-invalid={hexInput.invalid || undefined}
            aria-describedby={hexInput.invalid ? hexInput.errorId : undefined}
            data-testid={`color-role-hex-${name}`}
            style={{
              ...fieldControlStyle,
              flex: 1,
              minWidth: 0,
              padding: `var(--dcm-cms-color-role-select-padding, ${FIELD_CONTROL_PADDING})`,
              borderColor:
                'var(--dcm-cms-color-role-border-color, var(--puck-field-color-border, var(--puck-color-border, #d0d4d8)))',
              borderRadius: `var(--dcm-cms-color-role-border-radius, ${FIELD_CONTROL_RADIUS})`,
              fontSize: `var(--dcm-cms-color-role-font-size, ${FIELD_CONTROL_FONT_SIZE})`,
              fontFamily: 'var(--dcm-cms-color-role-font-family, monospace)',
            }}
          />
        </div>
      )}
      {isCustom && hexInput.invalid && <HexColorError id={hexInput.errorId} />}
    </FieldLabel>
  );
}
